import {
  GetBusinessInfoResponse,
  MyBusiness,
} from "@1games-ru/region-shared/dist/backend/requests/business";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { Button, Icon } from "@/shared/ui";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import FeeForm from "@/shared/widgets/FeeForm";
import FeeSuccess from "@/shared/widgets/FeeSuccess";
import PayRulesModal from "@/shared/widgets/PayRulesModal";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import useFeeHandlers from "@/domains/business/hooks/useFeeHandlers";
import BusinessView from "@/domains/business/components/BusinessView";

type Props = {
  info: MyBusiness | GetBusinessInfoResponse;
  onClose: () => void;
  device: DeviceEnum;
};

export default function BusinessFeeContainer({ info, onClose, device }: Props) {
  const { t } = useTranslation("business");

  const [isOpenRules, setIsOpenRules] = useState(false);

  const { isPending, payFeeHandler, setSelectedDays, selectedDays, getFeeAmountBySelectedDays } =
    useFeeHandlers(info, device);

  const openRules = useCallback(() => setIsOpenRules(true), []);
  const closeRules = useCallback(() => setIsOpenRules(false), []);

  if (!info) return null;

  const maxDays = Math.ceil((info.maxFee - info.feeBalance) / info.totalFee);
  const paidDays = info ? Math.floor(info.feeBalance / info.totalFee) : 0;

  const rules = (
    <button
      type="button"
      className="flex items-center gap-1.5 text-15 font-medium text-gray/40 transition-all hover:text-gray"
      onClick={openRules}
    >
      <Icon name="info" className="w-4" />
      <div className="underline">{t("business.fee.rules.openButton")}</div>
    </button>
  );

  const footer =
    maxDays === 0 ? (
      <FeeSuccess days={paidDays} i18FileName="business">
        {rules}
      </FeeSuccess>
    ) : (
      <div className="flex flex-col gap-5">
        <div className="flex items-end justify-between">
          <div className="flex flex-col gap-1.5">
            <div className="text-20 font-bold text-accent-solid">
              {formatPrice(getFeeAmountBySelectedDays())} ₽
            </div>
            <div className="text-14 font-medium text-white/50">{t("business.fee.payAmount")}</div>
          </div>
          {rules}
        </div>
        <Button
          className="h-[2.75rem] w-full flex items-center gap-2.5"
          onClick={payFeeHandler}
          loading={isPending}
          disabled={isPending || maxDays === 0}
        >
          <Icon name="card" category="hud" className="w-4.5" />
          {t("business.fee.pay")}
        </Button>
      </div>
    );

  return (
    <DeviceTabContentWrapper header={t("business.tabs.fee")} footer={footer} onClose={onClose}>
      <BusinessView info={info} />
      <FeeForm
        maxDays={maxDays}
        totalFee={info.totalFee}
        selectedDays={selectedDays}
        setSelectedDays={setSelectedDays}
        i18fileName="business"
        className="pt-5"
        color="lightBlue"
      />
      <PayRulesModal
        open={isOpenRules}
        onClose={closeRules}
        container="tablet"
        i18FileName="business"
        style={{ boxShadow: "0px 0px 180px 0px rgba(1, 100, 172, 0.30)" }}
        dataMzAccent="lightBlue"
      />
    </DeviceTabContentWrapper>
  );
}
